import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Grid,
  BarChart,
  TrendingUp,
  Check,
  ArrowRight,
  Star,
  ChevronDown,
  ChevronRight,
} from "lucide-react";

function FinancialSupportLevels() {
  const [activeLevel, setActiveLevel] = useState(0);
  const [expandedCard, setExpandedCard] = useState(null);

  const levels = [
    {
      id: 0,
      title: "Virtual Finance Manager",
      subtitle: "Perfect for startups needing a solid financial foundation.",
      features: [
        "High-tech software integration",
        "Weekly bookkeeping to keep your records up to date",
        "Fast month-end close for timely reporting",
        "Audit-proof & data room-ready books and reports for compliance and investor confidence",
      ],
      Icon: Grid,
      bgColor: "bg-red-50",
      iconColor: "text-red-500",
    },
    {
      id: 1,
      title: "Virtual Finance Controller",
      subtitle: "Ideal for businesses ready to streamline operations.",
      features: [
        "Monthly board pack preparation for clear insights",
        "Sales invoicing sorted to keep revenue flowing",
        "Supplier payments without the hassle for smooth transactions",
        "Cash monitoring to maintain financial health",
      ],
      Icon: BarChart,
      bgColor: "bg-red-50",
      iconColor: "text-red-500",
    },
    {
      id: 2,
      title: "Virtual Finance Director",
      subtitle: "Designed for startups aiming for strategic growth.",
      features: [
        "Annual 1-page plan to align your financial goals",
        "Cash flow forecast to plan for the future",
        "KPI dashboard to track performance",
        "Quarterly board meeting participation for expert input",
      ],
      Icon: TrendingUp,
      bgColor: "bg-red-50",
      iconColor: "text-red-500",
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15,
      },
    },
  };

  const featureVariants = {
    hidden: { opacity: 0, x: -20 },
    show: {
      opacity: 1,
      x: 0,
      transition: {
        type: "spring",
        stiffness: 100,
      },
    },
  };

  return (
    <section className="py-24 bg-gradient-to-br from-[#f8fafc] to-[#f1f5f9] relative overflow-hidden">
      <div className="absolute inset-0 z-0 overflow-hidden">
        {[...Array(8)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute rounded-full bg-gradient-to-r from-red-200/20 to-blue-200/20"
            style={{
              width: `${Math.random() * 120 + 60}px`,
              height: `${Math.random() * 120 + 60}px`,
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -30, 0],
              x: [0, 20, 0],
              rotate: [0, 180, 360],
            }}
            transition={{
              duration: 20 + Math.random() * 10,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
        ))}
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12 md:mb-16"
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            Our Financial Support Levels
          </h2>
          <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
            Tailored financial solutions to match your startup's growth stage
          </p>
        </motion.div>

        {/* Desktop View - Horizontal Cards */}
        <div className="hidden lg:block">
          <motion.div
            className="grid grid-cols-1 lg:grid-cols-3 gap-8"
            variants={containerVariants}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
          >
            {levels.map((level, index) => (
              <motion.div
                key={level.id}
                variants={cardVariants}
                className={`relative bg-white rounded-2xl shadow-lg border-2 transition-all duration-300 hover:shadow-2xl group ${
                  level.popular
                    ? "border-red-500 scale-105"
                    : "border-gray-200 hover:border-red-300"
                }`}
                whileHover={{ y: -5 }}
              >
                <div className="p-6 md:p-8">
                  <div
                    className={`${level.bgColor} p-4 rounded-xl mb-6 inline-block`}
                  >
                    <level.Icon className={`w-8 h-8 ${level.iconColor}`} />
                  </div>

                  <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">
                    {level.title}
                  </h3>
                  <p className="text-gray-600 mb-4">{level.subtitle}</p>
                  <div className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
                    {level.price}
                  </div>

                  <div className="space-y-3 mb-8">
                    {level.features.map((feature, idx) => (
                      <motion.div
                        key={idx}
                        className="flex items-start"
                        variants={featureVariants}
                      >
                        <div className="bg-green-100 p-1 rounded-full mt-0.5 mr-3 flex-shrink-0">
                          <Check className="text-green-500 w-3 h-3" />
                        </div>
                        <span className="text-gray-600 text-sm">{feature}</span>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>

        {/* Mobile/Tablet View - Expandable Cards */}
        <div className="lg:hidden">
          <motion.div
            className="space-y-4"
            variants={containerVariants}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
          >
            {levels.map((level, index) => (
              <motion.div
                key={level.id}
                variants={cardVariants}
                className={`bg-white rounded-2xl shadow-lg border-2 transition-all duration-300 ${
                  level.popular ? "border-red-500" : "border-gray-200"
                } ${expandedCard === level.id ? "shadow-2xl" : ""}`}
              >
                <div className="p-6">
                  <div
                    className="flex items-center justify-between cursor-pointer"
                    onClick={() =>
                      setExpandedCard(
                        expandedCard === level.id ? null : level.id
                      )
                    }
                  >
                    <div className="flex items-center">
                      <div className={`${level.bgColor} p-3 rounded-xl mr-4`}>
                        <level.Icon className={`w-6 h-6 ${level.iconColor}`} />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-gray-900">
                          {level.title}
                        </h3>
                        <p className="text-sm text-gray-600">{level.price}</p>
                      </div>
                    </div>
                    <motion.div
                      animate={{ rotate: expandedCard === level.id ? 180 : 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    </motion.div>
                  </div>

                  <AnimatePresence>
                    {expandedCard === level.id && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden"
                      >
                        <div className="pt-6 border-t border-gray-200 mt-6">
                          <p className="text-gray-600 mb-4">{level.subtitle}</p>

                          <div className="space-y-3 mb-6">
                            {level.features.map((feature, idx) => (
                              <motion.div
                                key={idx}
                                className="flex items-start"
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: idx * 0.1 }}
                              >
                                <div className="bg-green-100 p-1 rounded-full mt-0.5 mr-3 flex-shrink-0">
                                  <Check className="text-green-500 w-3 h-3" />
                                </div>
                                <span className="text-gray-600 text-sm">
                                  {feature}
                                </span>
                              </motion.div>
                            ))}
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
}

export default FinancialSupportLevels;
